import type { AdData } from "@adland/data";
import {
  type ActionAdapter,
  type AdConsent,
  createReadClient,
  fetchAdByKey,
  fetchAdFromURI,
  readAdURIForSlot,
  trackClick,
  trackImpression,
  trackVerifiedImpression,
} from "@adland/react/core";
import {
  adWebUrl,
  BASE_STYLES,
  cardMarkup,
  emptyMarkup,
  escapeAttr,
} from "./render";
import { readThemeVars } from "./theme";

const DEFAULT_CHAIN = 8453;
/**
 * Where an unsold slot sends a reader.
 *
 * The 0xSlots app moved off its own subdomain and now lives under a path on the
 * apex domain, so the base carries `/app` and the slot path is appended to it.
 */
const DEFAULT_BASE_LINK = "https://0xslots.org/app";
const RESERVED_HEIGHT = "68px";

/**
 * Build the `<adland-slot>` custom-element class bound to a click-routing
 * adapter. The web CDN bundle binds the web adapter (no Farcaster); the
 * Farcaster bundle binds the Mini App adapter.
 */
export function makeAdlandSlot(
  adapter: ActionAdapter,
): CustomElementConstructor {
  return class AdlandSlot extends HTMLElement {
    static observedAttributes = [
      "slot",
      "chain",
      "placement",
      "render-mode",
      "gateway",
      "endpoint",
      "rpc-url",
      "consent",
      "verified",
      "base-link-url",
      "data-ad",
    ];

    private shadowInited = false;
    /**
     * Whether the framed markup is wrapped in a real `<a>`.
     *
     * The click handler used to assume it always was, and told `handleClick`
     * the URL had already been opened. That is only true when a destination
     * existed — and for four of the six ad types it did not, so the click was
     * tracked and then dropped on the floor. Recording what was actually
     * rendered lets the adapter take over when the anchor is missing.
     */
    private framedInAnchor = false;
    private cleanupImpression?: () => void;
    /**
     * The address this element is actually showing.
     *
     * The `slot` attribute when there is one, otherwise whatever `slot-key`
     * resolved to. Everything below the fetch needs an address — the impression
     * is recorded against one and the empty-slot CTA links to one — and a name
     * is not an address until the module has answered.
     */
    private resolvedSlot?: string;
    private messageHandler?: (e: MessageEvent) => void;
    private renderToken = 0;

    connectedCallback() {
      void this.render();
    }

    disconnectedCallback() {
      this.teardown();
    }

    attributeChangedCallback() {
      if (this.isConnected) void this.render();
    }

    private attr(name: string): string | undefined {
      return this.getAttribute(name) ?? undefined;
    }

    private chainId(): number {
      return Number(this.attr("chain") ?? DEFAULT_CHAIN) || DEFAULT_CHAIN;
    }

    private teardown() {
      this.cleanupImpression?.();
      this.cleanupImpression = undefined;
      if (this.messageHandler) {
        window.removeEventListener("message", this.messageHandler);
        this.messageHandler = undefined;
      }
    }

    /** Resolve the ad: inline `data-ad` JSON, else an on-chain read. */
    private async loadAd(): Promise<{
      data: AdData | null;
      cid: string | null;
      /** Set when a NAME was resolved; the address it pointed at. */
      slot?: string;
    }> {
      const staticData = this.attr("data-ad");
      if (staticData) {
        try {
          return { data: JSON.parse(staticData) as AdData, cid: null };
        } catch {
          return { data: null, cid: null };
        }
      }

      const slot = this.attr("slot");
      const slotKey = this.attr("slot-key");
      if (!slot && !slotKey) return { data: null, cid: null };

      const client = createReadClient(this.chainId(), this.attr("rpc-url"));

      // A NAME, resolved through the AdLand hook, in one call.
      //
      // Preferred over an address for the reason the whole registry exists: an
      // address pasted into a page is one nobody can ever change, so redeploying
      // a slot leaves every embed carrying it showing a dead space. `slot` still
      // wins when both are set, because a publisher who pinned an address did so
      // to opt out of trusting us with the pointer.
      if (!slot && slotKey) {
        const snap = await fetchAdByKey(client, slotKey, {
          gateway: this.attr("gateway"),
          adHook: this.attr("ad-hook"),
        });
        // The resolved address travels with it: the impression below is
        // recorded against a slot, and a name is not one.
        return snap
          ? { data: snap.data, cid: snap.cid, slot: snap.slot }
          : { data: null, cid: null };
      }

      const uri = await readAdURIForSlot(
        client,
        slot as string,
        this.attr("ad-hook"),
      );
      if (!uri) return { data: null, cid: null };
      return fetchAdFromURI(uri, this.attr("gateway"));
    }

    private async render() {
      const token = ++this.renderToken;
      this.teardown();

      // Reserve height up front so an async load doesn't shift the layout.
      if (!this.style.minHeight) this.style.minHeight = RESERVED_HEIGHT;

      let result: { data: AdData | null; cid: string | null; slot?: string };
      try {
        result = await this.loadAd();
      } catch {
        result = { data: null, cid: null };
      }
      // A newer render superseded this one while awaiting — bail.
      if (token !== this.renderToken || !this.isConnected) return;

      const inner = result.data ? cardMarkup(result.data) : emptyMarkup();
      const requested = this.attr("render-mode");
      const mode =
        requested === "inline" || requested === "light"
          ? requested
          : "isolated";
      if (mode === "inline") this.renderInline(inner, result.data);
      else if (mode === "light") this.renderLight(inner, result.data);
      else this.renderIsolated(inner, result.data);

      // The attribute if there was one, otherwise the address the name
      // resolved to. Recording an impression needs an address either way.
      const resolved = this.attr("slot") ?? result.slot;
      this.resolvedSlot = resolved;
      if (resolved) {
        const track =
          this.attr("verified") != null
            ? trackVerifiedImpression
            : trackImpression;
        this.cleanupImpression = track(this, {
          slot: resolved,
          chainId: this.chainId(),
          placement: this.attr("placement"),
          cid: result.cid,
          empty: !result.data,
          endpoint: this.attr("endpoint"),
          consent: this.attr("consent") as AdConsent | undefined,
        });
      }
    }

    private renderInline(inner: string, data: AdData | null) {
      const root =
        this.shadowRoot ??
        (this.shadowInited ? null : this.attachShadow({ mode: "open" }));
      if (!root) return;
      this.shadowInited = true;
      root.innerHTML = `<style>${BASE_STYLES}</style>${inner}`;
      const card = root.querySelector<HTMLElement>(".adland-card");
      card?.addEventListener("click", () => this.handleClick(data, false));
    }

    /**
     * Into the page itself — no iframe, no shadow root, no styles of ours.
     *
     * ── The mode that exists so you can use your own CSS ─────────────────────
     *
     * The other two isolate, and both isolate from the publisher as much as
     * from the advertiser. `isolated` seals the card in an iframe, where the
     * only way in is the `--adland-*` custom properties copied across the
     * document boundary. `inline` uses a shadow root, which custom properties
     * inherit through and `::part()` can reach — better, and still not a
     * stylesheet: Tailwind emits classes, and a class in the page cannot cross
     * a shadow boundary. So a publisher wanting `rounded-2xl` on the card had
     * no way to say it.
     *
     * Here the markup is ordinary children of `<adland-slot>`. Every class hook
     * — `.adland-card`, `.adland-media`, `.adland-title`, `.adland-description`
     * — is a normal element in the page, so a page stylesheet, a Tailwind
     * arbitrary variant, or a `cn()` on the host all apply directly.
     *
     * ── What you give up, and why it is not the default ──────────────────────
     *
     * Isolation runs both ways. In this mode the publisher's CSS can break the
     * ad, and a reset of theirs can leave an advertiser's creative unreadable
     * with nobody able to see it but their own visitors. `BASE_STYLES` is NOT
     * injected — in the light DOM those rules would leak to the whole page —
     * so an unstyled card is what arrives, and styling it is now the
     * publisher's job rather than their option.
     *
     * That is a real cost and it is why this is opt-in and named. A publisher
     * who wants it asks for it; everybody else keeps the sealed default.
     */
    private renderLight(inner: string, data: AdData | null) {
      // `innerHTML` rather than a parsed fragment: `inner` is built by
      // `cardMarkup`, which escapes every interpolated value — `escapeHtml` for
      // text, `escapeAttr` for attributes — so the only markup here is ours.
      this.innerHTML = inner;
      const card = this.querySelector<HTMLElement>(".adland-card");
      card?.addEventListener("click", () => this.handleClick(data, false));
    }

    private renderIsolated(inner: string, data: AdData | null) {
      this.replaceChildren();

      const url = data ? adWebUrl(data) : this.emptyUrl();
      this.framedInAnchor = !!url;
      const body = url
        ? `<a href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer" style="display:block;text-decoration:none;color:inherit">${inner}</a>`
        : inner;
      const theme = readThemeVars(this);

      /*
       * The frame is a viewport, and anything drawn outside the card's border
       * box is outside the viewport.
       *
       * On an ordinary page a shadow spills past its element and a `transform`
       * moves it over its neighbours; both are visible because the page has
       * room. Here the card is 100% of a body with no padding, so a
       * `--adland-shadow` of `6px 6px` and a `--adland-transform-hover` of
       * `translate(4px, 4px)` are simply cropped by the frame — which showed up
       * as the right border vanishing on hover, an effect with no cause
       * anywhere in the card's own CSS.
       *
       * `--adland-bleed` is the room to draw in. Zero by default, so nothing
       * moves for a theme that does not spill; a raised style sets it to at
       * least its shadow offset plus its hover travel. The height message below
       * reads `scrollHeight`, which includes body padding, so the frame still
       * sizes itself correctly.
       */
      const iframe = document.createElement("iframe");
      iframe.setAttribute(
        "sandbox",
        "allow-popups allow-popups-to-escape-sandbox allow-scripts",
      );
      iframe.setAttribute("title", "Advertisement");
      iframe.setAttribute("scrolling", "no");
      iframe.style.cssText = "width:100%;border:0;display:block;height:100%";
      iframe.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><style>:root{${theme}}html,body{margin:0;padding:0}body{padding:var(--adland-bleed,0)}${BASE_STYLES}</style></head><body>${body}<script>(function(){function send(t,x){parent.postMessage(Object.assign({__adland:t},x||{}),'*')}addEventListener('click',function(){send('click')});function size(){send('size',{height:document.documentElement.scrollHeight})}new ResizeObserver(size).observe(document.documentElement);size()})()</script></body></html>`;

      const handler = (e: MessageEvent) => {
        if (e.source !== iframe.contentWindow) return;
        const msg = e.data as { __adland?: string; height?: number };
        if (msg?.__adland === "size" && typeof msg.height === "number") {
          iframe.style.height = `${msg.height}px`;
          this.style.minHeight = "";
        } else if (msg?.__adland === "click") {
          // Only skip the adapter when an anchor really did the navigating.
          // Passing `true` unconditionally meant an ad with no anchor tracked
          // the click and went nowhere.
          this.handleClick(data, this.framedInAnchor);
        }
      };
      this.messageHandler = handler;
      window.addEventListener("message", handler);
      this.appendChild(iframe);
    }

    private baseLink(): string {
      return this.attr("base-link-url") ?? DEFAULT_BASE_LINK;
    }

    private emptyUrl(): string | null {
      const slot = this.resolvedSlot ?? this.attr("slot");
      if (!slot) return null;
      return `${this.baseLink()}/slots/${slot}?chain=${this.chainId()}`;
    }

    private handleClick(data: AdData | null, alreadyOpened: boolean) {
      const slot = this.resolvedSlot ?? this.attr("slot");
      const shared = {
        chainId: this.chainId(),
        placement: this.attr("placement"),
        endpoint: this.attr("endpoint"),
        consent: this.attr("consent") as AdConsent | undefined,
      };

      if (data) {
        if (slot) trackClick("click", { slot, cid: null, ...shared });
        if (!alreadyOpened) void adapter.performAdAction(data);
      } else if (slot) {
        void trackClick("click-empty", { slot, ...shared });
        if (!alreadyOpened) {
          void adapter.performEmptyAdAction(
            slot,
            this.chainId(),
            this.baseLink(),
          );
        }
      }
    }
  };
}
